import { motion } from "motion/react";
import { ph } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { getAnalyticsRegion } from "@marketing-sites/analytics/get-analytics-region";
import { triggerAnalyticsEvent } from "@marketing-sites/analytics/trigger-analytics-event";
import { TrackingAction, TrackingType } from "@marketing-sites/analytics/types";
import Image from "next/image";
import { Button, FinLogo } from "@marketing-sites/ui/primitives";
import { cn } from "@marketing-sites/utils/cn";
import AttachmentIcon from "@marketing-sites/constants/messenger-chrome/attachment.svg";
import GifIcon from "@marketing-sites/constants/messenger-chrome/gif.svg";
import MicrophoneIcon from "@marketing-sites/constants/messenger-chrome/microphone.svg";
import PathUpIcon from "@marketing-sites/constants/messenger-chrome/path-up.svg";
import SmileIcon from "@marketing-sites/constants/messenger-chrome/smile.svg";

import { useFirstScroll, useScrollToTop, useStagger } from "./hooks";
import { Attachment, NavIcon, THREAD_SCROLL } from "./primitives";
import type {
  DemoPlacement,
  DemoProduct,
  DemoTicket,
  Industry,
} from "./tickets";

const MESSENGER_SCROLL_IDS: Record<Industry, string> = {
  ecommerce: "evt_solutions_ecommerce_demo_messenger_scroll",
  technology: "evt_solutions_technology_demo_messenger_scroll",
  gaming: "evt_solutions_gaming_demo_messenger_scroll",
  fintech: "evt_solutions_fintech_demo_messenger_scroll",
};
const HOME_MESSENGER_SCROLL_IDS: Record<Industry, string> = {
  ecommerce: "evt_home_ecommerce_demo_messenger_scroll",
  technology: "evt_home_technology_demo_messenger_scroll",
  gaming: "evt_home_gaming_demo_messenger_scroll",
  fintech: "evt_home_fintech_demo_messenger_scroll",
};
const LIVE_DEMO_IDS: Record<Industry, string> = {
  ecommerce: "btn_solutions_ecommerce_demo_live_fin_demo",
  technology: "btn_solutions_technology_demo_live_fin_demo",
  gaming: "btn_solutions_gaming_demo_live_fin_demo",
  fintech: "btn_solutions_fintech_demo_live_fin_demo",
};
const HOME_LIVE_DEMO_IDS: Record<Industry, string> = {
  ecommerce: "btn_home_ecommerce_demo_live_fin_demo",
  technology: "btn_home_technology_demo_live_fin_demo",
  gaming: "btn_home_gaming_demo_live_fin_demo",
  fintech: "btn_home_fintech_demo_live_fin_demo",
};

function LiveDemoCard({
  industry,
  placement,
}: {
  industry: Industry;
  placement: DemoPlacement;
}) {
  return (
    <div className="flex flex-col items-center gap-2 self-stretch rounded-2xl bg-[#f1f1f1] px-4 pb-4 pt-5 text-center">
      <span className="text-[17px] font-semibold text-[#111]">
        <Trans>Try a live Fin demo</Trans>
      </span>
      <span className="text-[13px] leading-[1.45] text-black/55">
        <Trans>
          See how Fin answers real questions over chat and voice, across
          different industries.
        </Trans>
      </span>
      <Button
        analyticsId={
          placement === "home"
            ? HOME_LIVE_DEMO_IDS[industry]
            : LIVE_DEMO_IDS[industry]
        }
        href="/view-demos"
        className="bg-helpdesk mt-2 h-9 w-full rounded-lg text-[13px] font-semibold text-white transition-colors hover:bg-[color-mix(in_srgb,var(--color-helpdesk),black_20%)] hover:text-white active:bg-[color-mix(in_srgb,var(--color-helpdesk),black_30%)]"
      >
        <Trans>View Fin demo</Trans>
      </Button>
    </div>
  );
}

function ProductCard({ product }: { product: DemoProduct }) {
  const { i18n } = useLingui();
  return (
    <div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-2xl border border-black/[0.08] bg-white">
      <div className="relative h-[84px] bg-[#fefefe]">
        <Image
          src={product.image}
          alt={product.name}
          fill
          sizes="168px"
          className="object-contain p-2"
        />
      </div>
      <div className="flex flex-1 flex-col gap-1 p-3">
        <span className="text-[12px] font-semibold">{product.name}</span>
        <span className="text-[12px] text-black/70">
          {i18n._(product.price)}
        </span>
        <span className="text-[11px] leading-[1.35] text-black/55">
          {i18n._(product.description)}
        </span>
        <span
          className={cn(
            "mt-auto rounded-lg py-1.5 text-center text-[11px] font-medium",
            product.added ? "bg-[#111] text-white" : "bg-black/[0.05]",
          )}
        >
          {i18n._(product.cta)}
        </span>
      </div>
    </div>
  );
}

export function Messenger({
  ticket,
  industry,
  placement = "solutions",
  showLiveDemo = false,
}: {
  ticket: DemoTicket;
  industry: Industry;
  placement?: DemoPlacement;
  showLiveDemo?: boolean;
}) {
  const { i18n } = useLingui();
  const stagger = useStagger();
  const products = ticket.products;
  const containerRef = useScrollToTop(ticket.id);
  const onScroll = useFirstScroll(ticket.id, (target) =>
    triggerAnalyticsEvent({
      action: TrackingAction.Event,
      type: TrackingType.Element,
      text: ticket.id,
      analyticsId:
        placement === "home"
          ? HOME_MESSENGER_SCROLL_IDS[industry]
          : MESSENGER_SCROLL_IDS[industry],
      location: getAnalyticsRegion(target) ?? "Unknown",
    }),
  );

  return (
    <div className="flex h-full w-full flex-col overflow-hidden rounded-[20px] border border-black/10 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.04),0_12px_32px_rgba(0,0,0,0.08)]">
      <div
        aria-hidden
        className="flex h-12 shrink-0 items-center gap-2.5 border-b border-black/[0.06] px-4 text-black/60"
      >
        <NavIcon d="M14.5 6l-6 6 6 6" />
        <FinLogo.Symbol className="size-[18px] text-black" />
        <span className="text-[14px] font-semibold text-black">Fin</span>
        <span className="ml-auto flex items-center gap-3">
          <NavIcon d="M6 12h.01M12 12h.01M18 12h.01" />
          <NavIcon d="M7 7l10 10M17 7L7 17" />
        </span>
      </div>
      <div
        ref={containerRef}
        onScroll={onScroll}
        className={cn(
          "flex min-h-0 flex-1 flex-col px-3 py-4 [mask-image:linear-gradient(to_bottom,transparent,black_16px)]",
          THREAD_SCROLL,
        )}
      >
        <div className="flex flex-col gap-2">
          {!ticket.hideGreeting && (
            <p className="self-start rounded-2xl bg-[#f1f1f1] px-3.5 py-2.5 text-[13px]">
              <Trans>
                Hi there! I’m Fin, {ph({ companyName: ticket.company })}’s AI
                Agent. How can I help?
              </Trans>
            </p>
          )}
          {ticket.messages.map((message, index) => (
            <motion.div
              key={`${ticket.id}-m-${index}`}
              {...stagger(index)}
              className={cn(
                "flex flex-col gap-2",
                message.from === "customer" ? "items-end" : "items-start",
              )}
            >
              {message.attachment && (
                <Attachment attachment={message.attachment} />
              )}
              <p
                className={cn(
                  "max-w-[82%] rounded-2xl px-3.5 py-2.5 text-[13px] leading-[1.45]",
                  message.from === "customer"
                    ? "bg-[#111] text-white"
                    : "bg-[#f1f1f1]",
                )}
              >
                {i18n._(message.text)}
                {message.link && (
                  <span className="block underline">{message.link}</span>
                )}
              </p>
              {products && index === products.afterMessage && (
                <div className="flex gap-2 self-stretch">
                  {products.items.map((product) => (
                    <ProductCard key={product.name} product={product} />
                  ))}
                </div>
              )}
            </motion.div>
          ))}
          {showLiveDemo && (
            <motion.div
              key={`${ticket.id}-live-demo`}
              {...stagger(ticket.messages.length)}
              className="mt-2 flex"
            >
              <LiveDemoCard industry={industry} placement={placement} />
            </motion.div>
          )}
        </div>
      </div>
      <div aria-hidden className="shrink-0 p-3">
        <div className="flex flex-col rounded-2xl border border-black/10 px-3.5 py-2.5">
          <span className="text-[13px] text-black/45">
            <Trans>Ask your question…</Trans>
          </span>
          <div className="mt-1 flex items-center justify-between text-black/35">
            <span className="flex items-center gap-3.5">
              <AttachmentIcon className="size-3.5" />
              <SmileIcon className="size-3.5" />
              <GifIcon className="size-3.5" />
              <MicrophoneIcon className="size-3.5" />
            </span>
            <span className="flex size-7 items-center justify-center rounded-full bg-black/[0.08]">
              <PathUpIcon className="size-3.5" />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
